import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import styles from "./styles.module.scss";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Autocomplete from "@mui/material/Autocomplete";
import Switch from "@mui/material/Switch";
import Button from "@mui/material/Button";
import { useConfirm } from "../../../../../hooks/useConfirm";
import { IGroupForm, IGroupItem } from "../../../../../@types/group.type";
import {
  deleteGroup,
  getAvailableFeaturesAndAccessLevels,
  addOrUpdateGroup,
} from "../../../../../services/api/groups/groupsService";
import { storageUserGet, storageUserSave } from "../../../../../storage/storageUser";
import { storageAuthTokenSave } from "../../../../../storage/storageAuthToken";
import { useKeycloak } from "@react-keycloak/web/lib/useKeycloak";
import { TUserInfo } from "../../../../../@types/user-info.type";
import { IUserInformation } from "../../../../../@types/user-information.type";
import { ensureUser } from "../../../../../services/api/user/UserService";
import { searchConfigurationsByKeys } from "../../../../../services/api/Configurations/ConfigurationService";
import { ApiException } from "../../../../../services/api/ApiException";
import { IConfiguration } from "../../../../../@types/configuration.type";

const screenSchema = z.object({
  groupName: z
    .string()
    .min(1, "Nome é obrigatório")
    .max(60, "Nome deve ter no máximo 60 caracteres"),
  groupDescription: z
    .string()
    .max(500, "Nome deve ter no máximo 500 caracteres")
    .optional(),
  areaId: z.string(),
  costCenterId: z.string(),
  projectId: z.string(),
  projectStatusId: z.string(),
  positionId: z.string(),
  customerId: z.string(),
  allocationId: z.string(),
  permissionId: z.string(),
  userId: z.string(),
  resourceId: z.string(),
  tokenId: z.string(),
  syncId: z.string(),
  customFieldsId: z.string(),
  availabilityId: z.string(),
  holidaysId: z.string(),
  financialContractedId: z.string(),
  financialPlannedId: z.string(),
  accountConfigurationId: z.string(),
  skillsId: z.string(),
  reportsId: z.string(),
  auditId: z.string(),
});

export type ScreenData = z.infer<typeof screenSchema>;

interface FormGroupsProps {
  pageType: "create" | "update";
  groupData?: IGroupForm;
}
interface IComboBoxOption {
  key: string;
  text: string;
}
const featuresId = {
  area: "12227607-6e29-4c8b-8657-120f900df7f6",
  costCenter: "1c04b18f-3758-46bf-bab2-71879420c271",
  project: "450320e8-af6c-4d95-b744-4d128912f39b",
  projectStatus: "ada85055-215d-4f73-8bfe-a9f245dbfff0",
  position: "6b9a8810-2797-4200-a5ec-58e8a6f435b5",
  customer: "722dee64-f520-4407-ac11-85830bb321b7",
  allocation: "967f48ca-2d7b-4311-b024-dd0f6f96f47d",
  permission: "af6170f4-7e01-4aa3-9f80-7cb946a74c34",
  user: "cca77e20-fd2a-4fa7-9fc0-04ebe9c50696",
  resource: "becf4c9a-0294-4aba-94e3-679378d65bcd",
  token: "cf042423-5b04-45c8-a2ee-45f8b625aa2f",
  sync: "d7e542f9-378d-4b91-bfbf-45f4c77bf619",
  customFields: "bb3e7c01-b43c-43d4-8897-529242ffa00d",
  availability: "e9af42ee-ec71-4343-8437-9ce4b4dff207",
  holidays: "c3dac137-aec6-4473-a2b8-591517be4a53",
  financialContracted: "3f6c78a6-ff5f-48e3-8f44-ef29bbeb9d4b",
  financialPlanned: "4668c817-44a4-429f-b846-923efd262e24",
  accountConfiguration: "1c4d91b4-ef37-44e2-94b5-69ffb44946a7",
  skills: "7eeec458-51e4-4055-b91f-35273948833a",
  reports: "c53c5764-2fb0-4bf3-8a7e-30a78221a601",
  audit: "b6669f2d-dbbc-4bc5-af8c-3514b1fed807",
};

const accessLevelsId = {
  edit: "25d72bd8-0256-4c21-9a8e-ed84204c3e1d",
  editOwn: "506d84ea-146f-4e2a-a380-5132937f01fd",
  view: "704d4abc-f3f8-4700-ba3d-d8556859d82d",
  noAccess: "aafc1a91-f39d-4d14-b294-6fd628dcb289",
};

export const FormGroup: React.FC<FormGroupsProps> = ({
  pageType,
  groupData,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const { keycloak, initialized } = useKeycloak();
  const [isFinancialModeEnabled, setIsFinancialModeEnabled] = useState(false);

  const fetchFinancialStatus = async () => {
    try {
      setLoading(true);
      const response = await searchConfigurationsByKeys(["CONFIGURATION_ENABLE_FINANTIAL"]);

      if (response instanceof ApiException) {
        throw new Error(response.message);
      }

      const status = response.result?.find((config: IConfiguration) => config.key === "CONFIGURATION_ENABLE_FINANTIAL");

      return status?.value === "true";
    } catch (error) {
      console.error("Erro ao buscar status financeiro:", error);
    } finally {
      setLoading(false);
    }
  };

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ScreenData>({
    resolver: zodResolver(screenSchema),
    defaultValues: {
      groupName: groupData?.groupName || "",
      groupDescription: groupData?.groupDescription || "",
      areaId: groupData?.areaId || accessLevelsId.noAccess,
      costCenterId: groupData?.costCenterId || accessLevelsId.noAccess,
      projectId: groupData?.projectId || accessLevelsId.view,
      projectStatusId: groupData?.projectStatusId || accessLevelsId.view,
      positionId: groupData?.positionId || accessLevelsId.noAccess,
      customerId: groupData?.customerId || accessLevelsId.noAccess,
      allocationId: groupData?.allocationId || accessLevelsId.view,
      permissionId: groupData?.permissionId || accessLevelsId.noAccess,
      userId: groupData?.userId || accessLevelsId.noAccess,
      resourceId: groupData?.resourceId || accessLevelsId.noAccess,
      tokenId: groupData?.tokenId || accessLevelsId.noAccess,
      syncId: groupData?.syncId || accessLevelsId.noAccess,
      customFieldsId: groupData?.customFieldsId || accessLevelsId.noAccess,
      availabilityId: groupData?.availabilityId || accessLevelsId.noAccess,
      holidaysId: groupData?.holidaysId || accessLevelsId.noAccess,
      financialContractedId: groupData?.financialContractedId || accessLevelsId.noAccess,
      financialPlannedId: groupData?.financialPlannedId || accessLevelsId.noAccess,
      accountConfigurationId: groupData?.accountConfigurationId || accessLevelsId.noAccess,
      skillsId: groupData?.skillsId || accessLevelsId.noAccess,
      reportsId: groupData?.reportsId || accessLevelsId.noAccess,
      auditId: groupData?.auditId || accessLevelsId.noAccess
    },
  });
  const [optionsAccessLevel, setOptionsAccessLevel] = useState<
    IComboBoxOption[]
  >([]);
  const navigate = useNavigate();

  const loadOptions = async () => {
    try {
      const response = await getAvailableFeaturesAndAccessLevels();
      const accessLevelsOptions = response.result.availableAccessLevels.map(
        (accessLevel: IGroupItem) => ({
          key: accessLevel.id,
          text: accessLevel.name,
        })
      );
      setOptionsAccessLevel(accessLevelsOptions);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchFinancialStatus().then(result => {
      setIsFinancialModeEnabled(result as boolean);
      loadOptions();
    });
  }, []);

  const onSubmit = async (data: IGroupForm) => {
    const groupToApi = {
      id: groupData?.groupId,
      name: data?.groupName || "",
      description: data?.groupDescription || "",
      createdByUserId: pageType === "create" ? storageUserGet().id : undefined,
      updatedByUserId: pageType === "update" ? storageUserGet().id : undefined,
      features: (Object.keys(featuresId) as Array<keyof typeof featuresId>).map(
        (key) => ({
          id: featuresId[key],
          selectedAccessLevel: {
            id: data?.[`${key}Id`] || "",
          },
        })
      ),
    };

    try {
      setLoading(true);
      const response = await addOrUpdateGroup(groupToApi);
      if (response.statusCode === 200) {
        storageAuthTokenSave(keycloak.token ?? "");
        const user = (await keycloak.loadUserInfo()) as TUserInfo;
        let userInformation: IUserInformation = {
          firstname: user.given_name,
          lastname: user.family_name,
          email: user.email,
        };
        userInformation = await ensureUser(userInformation);
        user.id = userInformation.id as string;
        user.resourceId = userInformation.resourceId;
        user.features = userInformation.features;
        storageUserSave(user);

        toast.success("Grupo salvo com sucesso!");
        navigate("/configurations/permissions");
      } else {
        toast.error(
          `Não foi possível salvar o grupo! Tente novamente mais tarde.`
        );
      }
    } catch (error) {
      console.log(error)
      toast.error(`Erro: Não foi possível salvar o grupo!`);
    } finally {
      setLoading(false);
    }
  };

  const [openModalConfirm, setOpenModalConfirm] = useState(false);
  const handleOpenModalConfirm = () => setOpenModalConfirm(true);
  const handleCloseModalConfirm = () => setOpenModalConfirm(false);
  const { showConfirm, Confirm } = useConfirm(
    "Tem certeza que deseja excluir o grupo?",
    openModalConfirm,
    handleCloseModalConfirm
  );

  const handleDeleteClick = async () => {
    handleOpenModalConfirm();
    if (!groupData?.groupId) return;

    const isConfirm = await showConfirm();

    if (isConfirm) {
      try {
        const response = await deleteGroup(groupData?.groupId);
        if (response.statusCode === 200) {
          toast.success("Grupo excluído com sucesso!");
          navigate("/configurations/permissions");
        } else {
          toast.error("Não foi possível excluir o grupo. Tente novamente.");
        }
      } catch (error) {
        console.log(error);
        toast.error("Erro ao excluir grupo. Tente novamente.");
      }
    }
  };

  const switchOptions = {
    option1: accessLevelsId.noAccess,
    option2: accessLevelsId.edit,
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className={styles.formGroup}>
        <h3 className={styles.title}>Informações Gerais</h3>
        <div className={styles.fields}>
          <div className={styles.fieldName}>
            <Controller
              name="groupName"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Nome"
                  placeholder={
                    pageType === "create"
                      ? "Insira o nome do grupo aqui..."
                      : ""
                  }
                  InputLabelProps={{
                    shrink: true,
                  }}
                  value={field.value}
                  onChange={field.onChange}
                  error={!!errors.groupName}
                  helperText={errors.groupName?.message}
                  fullWidth
                />
              )}
            />
          </div>
          <div className={styles.fieldDescription}>
            <Controller
              name="groupDescription"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Descrição"
                  placeholder={
                    pageType === "create"
                      ? "Insira a descrição do grupo aqui..."
                      : ""
                  }
                  InputLabelProps={{
                    shrink: true,
                  }}
                  value={field.value}
                  onChange={field.onChange}
                  error={!!errors.groupDescription}
                  helperText={errors.groupDescription?.message}
                  fullWidth
                />
              )}
            />
          </div>
        </div>
        <h3 className={styles.title}>Telas</h3>

        <Paper elevation={3}>
          <div style={{ height: "100%" }}>
            <div>
              {/* <div style={{ paddingLeft: "150px", paddingRight: "150px" }}> */}
              {["projectId", "allocationId"].map((name) => (
                <Controller
                  key={name}
                  name={name as keyof ScreenData}
                  control={control}
                  render={({ field }) => (
                    <Grid container alignItems="center" spacing={2} mb={2}>
                      <Grid item xs={6}>
                        <h1 className={styles.titleAutoLabel}>
                          {name === "projectId"
                            ? "Projetos"
                            : name === "allocationId"
                              ? "Mapa de Alocações"
                              : ""}
                        </h1>
                      </Grid>
                      <Grid item xs={6}>
                        <Autocomplete
                          options={optionsAccessLevel}
                          getOptionLabel={(option) => option.text}
                          value={
                            optionsAccessLevel.find(
                              (option) => option.key === field.value
                            ) || null
                          }
                          onChange={(_, newValue) =>
                            field.onChange(newValue?.key || "")
                          }
                          renderOption={(props, option) => {
                            return (
                              <li {...props} key={option.key}>
                                {option.text}
                              </li>
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              className={styles.textField}
                              label="Permissões"
                              InputLabelProps={{
                                shrink: true,
                              }}
                              placeholder="Selecione uma permissão aqui..."
                              error={!!errors[name as keyof ScreenData]}
                              helperText={
                                errors[name as keyof ScreenData]?.message
                              }
                            />
                          )}
                          sx={{ width: "auto" }}
                        />
                      </Grid>
                    </Grid>
                  )}
                />
              ))}

              {[
                { name: "reportsId", label: "Relatórios", enabled: true },
                { name: "financialContractedId", label: "Financeiro Contratado", enabled: isFinancialModeEnabled },
                { name: "financialPlannedId", label: "Financeiro Planejado", enabled: isFinancialModeEnabled },
                { name: "customerId", label: "Clientes", enabled: true },
                { name: "areaId", label: "Áreas", enabled: true },
                { name: "costCenterId", label: "Centros de Custo", enabled: true },
                { name: "projectStatusId", label: "Status de Projetos", enabled: true },
                { name: "positionId", label: "Funções", enabled: true },
                { name: "permissionId", label: "Permissões", enabled: true },
                { name: "userId", label: "Usuários", enabled: true },
                { name: "resourceId", label: "Colaboradores", enabled: true },
                { name: "tokenId", label: "Tokens de API", enabled: true },
                { name: "syncId", label: "Sincronização de Dados", enabled: true },
                { name: "customFieldsId", label: "Campos Personalizados", enabled: true },
                { name: "availabilityId", label: "Disponibilidade Padrão", enabled: true },
                { name: "holidaysId", label: "Feriados", enabled: true },
                { name: "accountConfigurationId", label: "Configurações da Conta", enabled: true },
                { name: "auditId", label: "Auditoria", enabled: true },
              ].filter(x => x.enabled).map(({ name, label }) => (
                <Controller
                  key={name}
                  name={name as keyof ScreenData}
                  control={control}
                  render={({ field }) => (
                    <Grid
                      container
                      alignItems="center"
                      justifyContent="center"
                      spacing={2}
                      // sx={{ paddingTop: '18px' }}
                      mb={2}
                    >
                      <Grid item xs={6}>
                        <h1 className={styles.titleLabel}>{label}</h1>
                      </Grid>
                      <Grid item xs={6} container alignItems="center">
                        <Grid item>
                          <h1 className={styles.switchLabel}>Sem Acesso</h1>
                        </Grid>
                        <Grid item>

                          <Switch
                            checked={field.value === switchOptions.option2}
                            onChange={() =>
                              field.onChange(
                                field.value === switchOptions.option1
                                  ? switchOptions.option2
                                  : switchOptions.option1
                              )
                            }
                            color="primary"
                          />
                        </Grid>
                        <Grid item>
                          <h1 className={styles.switchLabel}>Editar</h1>
                        </Grid>
                      </Grid>
                    </Grid>
                  )}
                />
              ))}
            </div>
          </div>
        </Paper>

        <div className={styles.btnContainer}>
          {groupData?.groupId && (
            <Button
              variant="contained"
              color="secondary"
              type="button"
              onClick={handleDeleteClick}
              className={styles.btnsDeleted}
              disabled={loading}
            >
              Excluir Grupo
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            type="submit"
            className={styles.btnsSave}
            disabled={loading}
          >
            Salvar
          </Button>
        </div>
      </form>
      {Confirm}
    </>
  );
};
